import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { createAccount } from './accountService';

/**
 * Check if a user has completed onboarding
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<boolean>}
 */
export const hasCompletedOnboarding = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return false;
    }
    return userDoc.data().onboardingCompleted === true;
  } catch (error) {
    console.error('Error checking onboarding status:', error);
    return false;
  }
};

/**
 * Get user profile from Firestore
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<object|null>}
 */
export const getUserProfile = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) {
      return null;
    }
    return { id: userDoc.id, ...userDoc.data() };
  } catch (error) {
    console.error('Error getting user profile:', error);
    return null;
  }
};

/**
 * Create initial user profile after registration
 * Creates account automatically and links user to it
 * @param {string} userId - Firebase Auth UID
 * @param {object} userData - User data (email, displayName)
 * @returns {Promise<string>} - Account ID
 */
export const createUserProfile = async (userId, userData) => {
  try {
    // Create account for new user
    const accountId = await createAccount(userId, {
      companyName: '',
      companyAddress: '',
      companyPhone: '',
      companyEmail: userData.email || '',
      companyWebsite: '',
      accountType: 'free'
    });

    // Create user profile with accountId reference
    await setDoc(doc(db, 'users', userId), {
      email: userData.email,
      displayName: userData.displayName || null,
      accountId, // Link to account
      onboardingCompleted: false,
      dailyGenerationsUsed: 0,
      themeMode: 'light',
      acceptedTerms: {
        accepted: true,
        acceptedAt: serverTimestamp(),
        ipAddress: null, // Could be added via Cloud Function if needed
        userAgent: navigator.userAgent
      },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // DEPRECATED: Brand info - use workspaces instead
      // These fields maintained for backwards compatibility only
      brandName: null,
      brandLogoUrl: null,
      brandColors: {
        primary: '#9333ea',
        secondary: '#ec4899'
      }
    });

    return accountId;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

/**
 * Mark onboarding as completed for user
 * @param {string} userId - Firebase Auth UID
 * @param {object} brandData - DEPRECATED: Brand info now stored in workspaces
 * @returns {Promise<void>}
 */
export const completeOnboarding = async (userId, brandData) => {
  try {
    // Note: brandData parameter is deprecated. Brand info should be created
    // as a workspace instead. This function now only marks onboarding complete.
    const updates = {
      onboardingCompleted: true,
      updatedAt: serverTimestamp()
    };

    // Legacy support: still save brand data if provided (for backwards compatibility)
    if (brandData?.brandName) {
      updates.brandName = brandData.brandName;
      updates.brandLogoUrl = brandData.brandLogoUrl || null;
      updates.brandColors = brandData.brandColors;
    }

    await updateDoc(doc(db, 'users', userId), updates);
  } catch (error) {
    console.error('Error completing onboarding:', error);
    throw error;
  }
};

/**
 * DEPRECATED: Update user brand settings
 * @deprecated Use workspace updates instead. Brand data should be stored in workspaces.
 * @param {string} userId - Firebase Auth UID
 * @param {object} brandData - Updated brand data
 * @returns {Promise<void>}
 */
export const updateBrandSettings = async (userId, brandData) => {
  console.warn('updateBrandSettings is deprecated. Use workspace updates instead.');
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...brandData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating brand settings:', error);
    throw error;
  }
};

/**
 * Increment daily generation count
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<void>}
 */
export const incrementDailyGenerations = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const currentCount = userDoc.data().dailyGenerationsUsed || 0;
      await updateDoc(doc(db, 'users', userId), {
        dailyGenerationsUsed: currentCount + 1,
        updatedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error incrementing daily generations:', error);
    throw error;
  }
};

/**
 * Reset daily generation count (called by cron job daily)
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<void>}
 */
export const resetDailyGenerations = async (userId) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      dailyGenerationsUsed: 0,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error resetting daily generations:', error);
    throw error;
  }
};

/**
 * Update user's language preference
 * @param {string} userId - User ID
 * @param {string} languageCode - Language code (e.g., 'en', 'es')
 * @returns {Promise<void>}
 */
export const updateUserLanguage = async (userId, languageCode) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      language: languageCode,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user language:', error);
    throw new Error('Failed to update language preference');
  }
};
